// ============================================================
// THE CLOSING EFFECTS (plan body-map H3; the bench's, as the user ruled them
// on 2026-10-09): what plays after the last hit - morale (Pinning, Panicking,
// Cowing), the machine spirit's static (Haywire), the snare, the banes that
// strip armour (Armourbane, Breaching, Sunder), what cripples and drains
// (Crippling, Withering, Sapping, Numbing, Slowing), and what shakes the
// ground (Devastating, Titanbane). None of them moves the figure: another
// special owned the move (the bench's `specialFx` holds them still).
// ============================================================

import type { ReactNode } from 'react';
import clsx from '../../../../utils/clsx';
import sx from '../../../../styles/PlateSpecials.module.css';
import { jagged, Motes, swingEnds, type Mote, type Point } from './primitives';
import { around, forgeSparks, partGlow, wholeLines, type LookCtx } from './kit';
import { timed } from '../timing';

/** Pinning: rounds crack past it - tracers whipping by close on both sides (a Willpower test or Pinned). */
function pinning(c: LookCtx) {
  const [x0, y0, x1, y1] = c.figureBox;
  const mid = (x0 + x1) / 2;
  return Array.from({ length: 7 }, (_, i) => {
    const side = i % 2 ? 1 : -1;
    const x = mid + side * ((x1 - x0) * 0.55 + 60 * c.r(`px${i}`) * c.k);
    const y = y0 + (y1 - y0) * (0.1 + 0.7 * c.r(`py${i}`));
    const w = 6 * c.k;
    const [lo, hi, lean] = [y1 + 400 * c.k, y - 400 * c.k, x + side * 40 * c.k];
    const points = `${x},${lo} ${x + w},${lo} ${lean + w},${hi} ${lean},${hi}`;
    return (
      <polygon
        key={i}
        className={clsx(sx.smear, sx.tracerWarm)}
        points={points}
        filter={c.glow}
        style={timed(c.start + i * 0.08, 0.22)}
      />
    );
  });
}

/** A glow over the struck place reaching the whole body (a shiver, a dread). */
const bodyWide = (c: LookCtx, palette: string, dur: number) => partGlow(c, { r: 2000, palette, dur, filter: 'none' });

/** Panicking: a horde's rule; on one figure, only a cold shiver runs down it. */
function panicking(c: LookCtx) {
  return bodyWide(c, sx.cold, 1.2);
}

/** Cowing: its will beaten down for three rounds - its lines dimmed, and staying so a while. */
function cowing(c: LookCtx) {
  return wholeLines(c, sx.veil, timed(c.start));
}

/** Haywire: machine-spirit static bursts from the hit - the scan jams in bands, the figure split in two colours. */
function haywire(c: LookCtx) {
  const [x0, y0, x1, y1] = c.figureBox;
  const bars = Array.from({ length: 9 }, (_, i) => (
    <rect
      key={i}
      className={clsx(sx.static, i % 3 ? sx.staticA : sx.staticB)}
      x={x0 - 300 * c.k + (c.r(`hx${i}`) - 0.5) * 300 * c.k}
      y={y0 + (y1 - y0) * c.r(`hy${i}`)}
      width={x1 - x0 + 600 * c.k}
      height={(18 + 40 * c.r(`hh${i}`)) * c.k}
      style={timed(c.start + i * 0.04)}
    />
  ));
  return (
    <>
      {bars}
      {wholeLines(c, clsx(sx.split, sx.splitA), timed(c.start), { transform: `translate(${-40 * c.k} 0)` })}
      {wholeLines(c, clsx(sx.split, sx.splitB), timed(c.start), { transform: `translate(${40 * c.k} 0)` })}
    </>
  );
}

/** Snare: cords lash round the struck part, sized to it, draw tight, and dissolve. */
function snare(c: LookCtx) {
  const [x0, y0, x1, y1] = c.partBox;
  const rx = (x1 - x0) * 0.62 + 30 * c.k;
  return [0, 1, 2].map((i) => {
    const cy = c.at[1] + (i - 1) * Math.min(90 * c.k, (y1 - y0) * 0.15);
    return (
      <ellipse
        key={i}
        className={sx.cord}
        cx={(x0 + x1) / 2}
        cy={cy}
        rx={rx}
        ry={rx * 0.2}
        strokeWidth={9 * c.k}
        transform={`rotate(${(i - 1) * 10} ${c.at[0]} ${cy})`}
        style={timed(c.start + i * 0.08)}
      />
    );
  });
}

/** Armourbane (user): violent, sudden - a thick black cut snaps across the plate through the wound, shards fly. */
function armourbane(c: LookCtx) {
  const ang = (c.r('ab') - 0.5) * 0.6;
  // Right across the plate (8000: wider than any frame).
  const [A, B] = swingEnds(c.at, ang, 8000);
  const line = { x1: A[0], y1: A[1], x2: B[0], y2: B[1], pathLength: 1 };
  const shards = Array.from({ length: 12 }, (_, i): Mote => {
    const along = (c.r(`sa${i}`) - 0.5) * 900 * c.k;
    const from: Point = [c.at[0] + Math.cos(ang) * along, c.at[1] + Math.sin(ang) * along];
    return { from, by: [Math.cos(ang) * 260 * c.k * (i % 2 ? 1 : -1), 160 * c.k], delay: 0.05, r: 10 * c.k };
  });
  return (
    <>
      <line className={sx.cutBruise} {...line} strokeWidth={64 * c.k} filter={c.glow} style={timed(c.start)} />
      <line className={sx.cut} {...line} strokeWidth={34 * c.k} style={timed(c.start)} />
      <Motes motes={shards} palette={sx.shard} start={c.start} dur={0.8} />
    </>
  );
}

/** Breaching: through the plating - metal petals peel outward round a hole punched straight in. */
function breaching(c: LookCtx) {
  const [x, y] = c.at;
  const petals = Array.from({ length: 6 }, (_, i) => {
    const a = (i / 6) * Math.PI * 2;
    const p = (da: number, r: number) =>
      `${Math.round(x + r * c.k * Math.cos(a + da))},${Math.round(y + r * c.k * Math.sin(a + da))}`;
    return `${p(-0.4, 20)} ${p(0.4, 20)} ${p(0, 120)}`;
  });
  return (
    <>
      {petals.map((points, i) => (
        <polygon
          key={i}
          className={sx.petal}
          points={points}
          strokeWidth={4 * c.k}
          style={{ ...timed(c.start), transformOrigin: `${x}px ${y}px` }}
        />
      ))}
      <circle className={sx.breach} cx={x} cy={y} r={32 * c.k} strokeWidth={5 * c.k} style={timed(c.start)} />
    </>
  );
}

/** Sunder (user): the armour at the place is crushed - grey flakes fall, a hammer's sparks fly, its number drops. */
function sunder(c: LookCtx) {
  const flakes = Array.from({ length: 8 }, (_, i): Mote => ({
    from: around(c, 120, `fl${i}`),
    by: [0, 260 * c.k],
    delay: 0.1,
    r: 12 * c.k,
  }));
  return (
    <>
      <Motes motes={flakes} palette={sx.flakes} start={c.start} dur={1.6} />
      <Motes motes={forgeSparks(c, 42, 'f')} palette={sx.debris} start={c.start} dur={0.75} glow={c.glow} />
      <text
        className={clsx(sx.word, sx.debris)}
        x={c.at[0] + 140 * c.k}
        y={c.at[1] - 60 * c.k}
        fontSize={110 * c.k}
        style={timed(c.start + 0.3)}
      >
        AP -1
      </text>
    </>
  );
}

/** Crippling: a critical here cripples too - the part flashes the crippled amber and a crack runs along it. */
function crippling(c: LookCtx) {
  const [x0, , x1, y1] = c.partBox;
  const crack = jagged(c.at, [(x0 + x1) / 2, y1 - 40 * c.k], 7, 40 * c.k, (s) => c.r(`ck${s}`));
  return (
    <>
      {partGlow(c, { r: 420, palette: sx.crippled, dur: 1.6 })}
      <path className={sx.crack} d={crack} pathLength={1} strokeWidth={6 * c.k} style={timed(c.start + 0.1)} />
    </>
  );
}

/** Withering: its toughness drains for three rounds - the part's lines go a sickly brown, and stay so a while. */
function withering(c: LookCtx) {
  return partGlow(c, { r: 560, palette: sx.wither, dur: 3.2, width: 34, filter: 'none' });
}

/** Sapping: its strength drawn out - motes leave the body and drift away, and it dims a moment. */
function sapping(c: LookCtx) {
  const [x0, y0, x1, y1] = c.figureBox;
  const motes = Array.from({ length: 18 }, (_, i): Mote => {
    const from: Point = [x0 + (x1 - x0) * c.r(`sx${i}`), y0 + (y1 - y0) * (0.2 + 0.6 * c.r(`sy${i}`))];
    return { from, by: [c.at[0] - 900 * c.k - from[0], c.at[1] - 200 * c.k - from[1]], delay: i * 0.04, r: 8 * c.k };
  });
  return (
    <>
      <Motes motes={motes} palette={sx.sap} start={c.start} dur={1.2} glow={c.glow} />
      {wholeLines(c, sx.veil, timed(c.start, 1.2))}
    </>
  );
}

/** Numbing: its hands and eyes go numb - a cold blue frost creeps over the arms, and the head blurs. */
function numbing(c: LookCtx) {
  const on = (part: string, look: { r: number; palette: string; dur: number; filter: string }) => {
    const at = c.anchors[part];
    return at && partGlow(c, { ...look, part, at });
  };
  const frost = { r: 520, palette: sx.frost, dur: 2.4, filter: 'none' };
  return (
    <>
      {on('rightArm', frost)}
      {on('leftArm', frost)}
      {on('head', { r: 400, palette: sx.cold, dur: 1.6, filter: `url(#${c.u}-numb)` })}
    </>
  );
}

/** Slowing: it is slowed - a heavy drag of ghosts trailing behind it, catching up late. */
function slowing(c: LookCtx) {
  return [1, 2, 3, 4].map((k) => (
    <g key={k}>
      {wholeLines(c, sx.drag, timed(c.start + k * 0.12, 0, { '--lag': -60 * k * c.k, '--from': 0.4 - 0.07 * k }))}
    </g>
  ));
}

/** Rings running out across the floor from the feet. */
function floorRings(c: LookCtx, n: number, reach: number, stagger: number) {
  const [x0, , x1, y1] = c.figureBox;
  return Array.from({ length: n }, (_, k) => (
    <ellipse
      key={k}
      className={sx.floorRing}
      cx={(x0 + x1) / 2}
      cy={y1 + 20 * c.k}
      rx={reach * c.k}
      ry={reach * 0.16 * c.k}
      strokeWidth={Math.max(2, 14 - 5 * k) * c.k}
      style={timed(c.start + k * stagger)}
    />
  ));
}

/** Devastating: against a horde it fells more - here, a heavy shock runs out across the floor from its feet. */
function devastating(c: LookCtx) {
  return floorRings(c, 2, 1800, 0.15);
}

/** Titanbane (user): the ground shakes - floor lines judder, shock rings run out, dust rises; more with each size. */
function titanbane(c: LookCtx): ReactNode {
  const steps = c.sizeSteps;
  if (!steps) return null;
  const [x0, , x1, y1] = c.figureBox;
  const floor = Array.from({ length: 5 }, (_, k) => (
    <line
      key={k}
      className={sx.judder}
      x1={x0 - 1200 * c.k}
      x2={x1 + 1200 * c.k}
      y1={y1 + (20 + k * 55) * c.k}
      y2={y1 + (20 + k * 55) * c.k}
      strokeWidth={4 * c.k}
      style={timed(c.start, 0, { '--amp': 18 * steps * c.k, '--from': 0.5 - 0.08 * k })}
    />
  ));
  const dust = Array.from({ length: 6 + 3 * steps }, (_, k): Mote => ({
    from: [(x0 + x1) / 2 + (c.r(`dx${k}`) - 0.5) * (x1 - x0) * 1.2, y1 + 20 * c.k],
    by: [0, -(120 + 120 * c.r(`dy${k}`)) * c.k],
    delay: 0.05 * k,
    r: 40 * c.k,
  }));
  return (
    <>
      {floor}
      {floorRings(c, 2 + steps, 1500 + 300 * steps, 0.14)}
      <Motes motes={dust} palette={sx.dust} start={c.start} dur={1.4} />
    </>
  );
}

/** The closing effects' looks, by key. */
export const CLOSING_LOOKS: Record<string, (c: LookCtx) => ReactNode> = {
  pinning,
  panicking,
  cowing,
  haywire,
  snare,
  armourbane,
  breaching,
  sunder,
  crippling,
  withering,
  sapping,
  numbing,
  slowing,
  devastating,
  titanbane,
};
